import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import transactionChangeService from "services/transactionChangeService";
import _set from "lodash/set";

export const getAllInitiateTransactionChangeAsync = createAsyncThunk(
  "transactionChange/getAllInitiateTransactionChangeAsync",
  async ({ companyId, params }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.getAll(companyId, params);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSpecificInitiatedTransactionChangeAsync = createAsyncThunk(
  "transactionChange/getSpecificInitiatedTransactionChangeAsync",
  async ({ companyId, createdBy }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.get(companyId, createdBy);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createTransactionChangeAsync = createAsyncThunk(
  "transactionChange/createTransactionChangeAsync",
  async ({ data, companyId }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.create(companyId, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getTransactionChangeByIdAsync = createAsyncThunk(
  "transactionChange/getTransactionChangeByIdAsync",
  async ({ trxId }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.getById(trxId);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getTransactionChangeByBatchNumberAsync = createAsyncThunk(
  "transactionChange/getTransactionChangeByBatchNumberAsync",
  async ({ batchNo }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.getByBatchNumber(batchNo);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editInitiatedTransactionChangeAsync = createAsyncThunk(
  "transactionChange/editInitiatedTransactionChangeAsync",
  async ({ companyId, formData }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.update(companyId, formData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const approveTransactionByDirectorAsync = createAsyncThunk(
  "transactionChange/approveTransactionByDirectorAsync",
  async ({ companyId, data }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.updateByDirector(
        companyId,
        data
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const changeDocumentSignatureUpload = createAsyncThunk(
  "transactionChange/changeDocumentSignatureUpload",
  async ({ companyId, data }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.changeDocumentSignature(
        companyId,
        data
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const transactionReviewAsync = createAsyncThunk(
  "transactionChange/transactionReviewAsync",
  async ({ companyId, data }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.transactionReview(
        companyId,
        data
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteInitiatedTransactionChangeAsync = createAsyncThunk(
  "transactionChange/InitiatedTransactionChangeAsync",
  async ({ companyId, initiateId }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.remove(companyId, initiateId);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const recallInitiatedTransactionChangeAsync = createAsyncThunk(
  "transactionChange/recallInitiatedTransactionChangeAsync",
  async ({ companyId, data }, { rejectWithValue }) => {
    try {
      const res = await transactionChangeService.transactionRecall(
        companyId,
        data
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  initiatedTransactions: {
    items: [],
    specificItem: [],
    transactionById: {},
    totalCount: 0,
    batchNo: null,
  },
  batchedTransactions: {
    items: [],
    totalCount: 0,
  },
  getBatchedTransactions: {
    status: "idle",
    error: false,
  },
  getInitiatedTransactions: {
    status: "idle",
    error: false,
  },
  specificItemTransaction: {
    status: "idle",
    error: false,
  },
  getTransactionById: {
    status: "idle",
    error: false,
  },
  initiateNewTransaction: {
    status: "idle",
    error: false,
  },
  approvedTransactionByDirectorStatus: {
    status: "idle",
    error: false,
  },
  temporaryStorage: {
    items: [],
  },
  recentChangesToggle: {
    status: false,
  },
};

const initiatedTransactionsSlice = createSlice({
  name: "transactionChange",
  initialState,
  reducers: {
    resetTransactionChange: () => initialState,
    addToTemporaryStorage: (state, action) => {
      state.temporaryStorage.items.push(action.payload);
    },
    updateManageCompanyOfficialsNestedField: (
      state,
      { payload: { newNestedField, updateEffectPath } }
    ) => {
      return _set(state, updateEffectPath.split(".").slice(1), newNestedField);
    },
    getTemporaryStorage: (state, action) => state,
    clearTemporaryStorage: (state) => {
      state.temporaryStorage.items = [];
    },
    recentChangesHandler: (state, action) => {
      state.recentChangesToggle.status = !state.recentChangesToggle.status;
    },
    getRecentChangesStatus: (state) => state,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllInitiateTransactionChangeAsync.pending, (state) => {
        state.getInitiatedTransactions.status = "loading";
        state.getInitiatedTransactions.error = false;
      })
      .addCase(
        getAllInitiateTransactionChangeAsync.fulfilled,
        (state, action) => {
          state.getInitiatedTransactions.status = "idle";
          state.initiatedTransactions = action.payload;
          if (action.payload.totalCount > 0) {
            state.initiatedTransactions.batchNo =
              action.payload.items[0]?.batchNo;
          }
        }
      )
      .addCase(
        getAllInitiateTransactionChangeAsync.rejected,
        (state, action) => {
          state.getInitiatedTransactions.status = "failed";
          state.getInitiatedTransactions.error = action?.payload?.messageText;
        }
      )
      .addCase(getTransactionChangeByBatchNumberAsync.pending, (state) => {
        state.getBatchedTransactions.status = "loading";
        state.getBatchedTransactions.error = false;
      })
      .addCase(
        getTransactionChangeByBatchNumberAsync.fulfilled,
        (state, action) => {
          state.getBatchedTransactions.status = "idle";
          state.batchedTransactions = action.payload;
        }
      )
      .addCase(
        getTransactionChangeByBatchNumberAsync.rejected,
        (state, action) => {
          state.getBatchedTransactions.status = "failed";
          state.getBatchedTransactions.error = action?.payload?.messageText;
        }
      )
      .addCase(getSpecificInitiatedTransactionChangeAsync.pending, (state) => {
        state.specificItemTransaction.status = "loading";
        state.specificItemTransaction.error = false;
      })
      .addCase(
        getSpecificInitiatedTransactionChangeAsync.fulfilled,
        (state, action) => {
          state.specificItemTransaction.status = "idle";
          state.initiatedTransactions.specificItem = action.payload;
        }
      )
      .addCase(
        getSpecificInitiatedTransactionChangeAsync.rejected,
        (state, action) => {
          state.specificItemTransaction.status = "failed";
          state.specificItemTransaction.error = action?.payload?.messageText;
        }
      )
      .addCase(getTransactionChangeByIdAsync.pending, (state) => {
        state.getTransactionById.status = "loading";
        state.getTransactionById.error = false;
      })
      .addCase(getTransactionChangeByIdAsync.fulfilled, (state, action) => {
        state.getTransactionById.status = "idle";
        state.initiatedTransactions.transactionById = action.payload;
      })
      .addCase(getTransactionChangeByIdAsync.rejected, (state, action) => {
        state.getTransactionById.status = "failed";
        state.getTransactionById.error = action?.payload?.messageText;
      })
      .addCase(createTransactionChangeAsync.pending, (state, action) => {
        state.initiateNewTransaction.status = "loading";
        state.initiateNewTransaction.error = false;
      })
      .addCase(createTransactionChangeAsync.fulfilled, (state, action) => {
        if (action.payload.savedTransaction) {
          state.initiatedTransactions.items.push(
            action.payload.savedTransaction
          );
          state.initiatedTransactions.totalCount += 1;
        }
        state.initiateNewTransaction.status = "idle";
      })
      .addCase(createTransactionChangeAsync.rejected, (state, action) => {
        state.initiateNewTransaction.status = "failed";
        state.initiateNewTransaction.error = action?.payload?.messageText;
      })
      .addCase(
        editInitiatedTransactionChangeAsync.fulfilled,
        (state, action) => {
          const { payload } = action;
          const index = state.initiatedTransactions.items.findIndex(
            (transaction) => payload._id === transaction._id
          );
          if (index > -1) {
            state.initiatedTransactions.items.splice(index, 1, payload);
          }
        }
      )
      .addCase(approveTransactionByDirectorAsync.fulfilled, (state, action) => {
        const { payload } = action;
        const index = state.initiatedTransactions.items.findIndex(
          (transaction) => payload._id === transaction._id
        );
        if (index > -1) {
          state.initiatedTransactions.items.splice(index, 1, payload);
        }
      })
      .addCase(changeDocumentSignatureUpload.fulfilled, (state, action) => {
        const { payload } = action;
        const index = state.initiatedTransactions.items.findIndex(
          (transaction) => payload._id === transaction._id
        );
        if (index > -1) {
          state.initiatedTransactions.items.splice(index, 1, payload);
        }
      })
      .addCase(transactionReviewAsync.fulfilled, (state, action) => {
        const { payload } = action;
        const index = state.initiatedTransactions.items.findIndex(
          (transaction) => payload._id === transaction._id
        );
        if (index > -1) {
          state.initiatedTransactions.items.splice(index, 1, payload);
        }
      })
      .addCase(
        deleteInitiatedTransactionChangeAsync.fulfilled,
        (state, action) => {
          const { payload } = action;
          const index = state.initiatedTransactions.items.findIndex(
            (transaction) => payload._id === transaction._id
          );
          if (index > -1) {
            state.initiatedTransactions.items.splice(index, 1, payload);
          }
        }
      );
  },
});

export const {
  resetTransactionChange,
  addToTemporaryStorage,
  getTemporaryStorage,
  clearTemporaryStorage,
  recentChangesHandler,
  getRecentChangesStatus,
  updateManageCompanyOfficialsNestedField,
} = initiatedTransactionsSlice.actions;

const { reducer } = initiatedTransactionsSlice;

export default reducer;
